<script setup lang="ts">
import { getReturnsAddress } from '~~/app/brands'

defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

// Moduł odstąpienia od umowy „Wygodne Zwroty" — per-brand (patrz useReturns).
// Fallback na formularz nadania przesyłki, gdy brand nie ma jeszcze modułu.
const { externalUrl, withdrawalUrl } = useReturns()
const WYGODNE_ZWROTY_URL = withdrawalUrl || externalUrl

// Dane firmy per-brand (adres zwrotu, kontakt) — jak w SiteFooter/contact.
const brand = useBrand()
// Leniwie — szuflada liczy adres dopiero po otwarciu, tak jak wcześniej szablon.
const returnsAddress = computed(() => getReturnsAddress(brand))
</script>

<template>
  <Transition name="returns-drawer">
    <div
      v-if="isOpen"
      class="returns-drawer"
      role="dialog"
      aria-modal="true"
      aria-label="Bezpłatny zwrot do 14 dni"
    >
      <button
        class="returns-drawer__backdrop"
        type="button"
        aria-label="Zamknij klikając tło"
        @click="emit('close')"
      />

      <aside class="returns-drawer__panel">
        <header class="returns-drawer__header">
          <h2 class="returns-drawer__title">Bezpłatny zwrot do 14 dni</h2>
          <button
            type="button"
            class="returns-drawer__close"
            aria-label="Zamknij"
            @click="emit('close')"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              aria-hidden="true"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div class="returns-drawer__content">
          <p class="returns-drawer__intro">
            Produkt nie spełnia Twoich oczekiwań? Poniżej instrukcja zwrotu.
          </p>

          <section class="returns-drawer__section">
            <h3 class="returns-drawer__subtitle">Instrukcja zwrotu:</h3>
            <!-- Marka bez konta „Wygodne Zwroty" (discanto) nie ma formularza
                 online — przycisk miałby pusty href. -->
            <template v-if="WYGODNE_ZWROTY_URL">
              <p class="returns-drawer__text">
                Zgłoś odstąpienie od umowy w formularzu online — tam też wygodnie
                nadasz przesyłkę zwrotną:
              </p>
              <a
                class="returns-drawer__button"
                :href="WYGODNE_ZWROTY_URL"
                target="_blank"
                rel="noopener noreferrer"
              >
                Odstąp od umowy i zwróć produkty
              </a>
              <p class="returns-drawer__text">
                lub na adres: {{ returnsAddress.street }}, {{ returnsAddress.postalCode }} {{ returnsAddress.city }}
              </p>
            </template>
            <p v-else class="returns-drawer__text" data-testid="returns-drawer-offline">
              Napisz na
              <a v-mailto="brand.footer.email">{{ brand.footer.email }}</a>,
              że odstępujesz od umowy, i odeślij produkty na adres:
              {{ returnsAddress.name }}, {{ returnsAddress.street }},
              {{ returnsAddress.postalCode }} {{ returnsAddress.city }}
            </p>
          </section>

          <section class="returns-drawer__section">
            <h3 class="returns-drawer__subtitle">Podstawowe informacje:</h3>
            <p class="returns-drawer__text">
              <strong>14 dni</strong> na odstąpienie od umowy bez podawania
              przyczyny
            </p>
            <p class="returns-drawer__text">
              <strong>Bezpłatny zwrot</strong> (z wyjątkiem kosztów określonych
              w regulaminie)
            </p>
            <p class="returns-drawer__text">
              <strong>Zwrot pieniędzy</strong> w ciągu 14 dni od otrzymania
              oświadczenia
            </p>
          </section>

          <p class="returns-drawer__contact">
            <strong>Kontakt:</strong>
            <a v-mailto="brand.footer.email">{{ brand.footer.email }}</a>
          </p>
        </div>
      </aside>
    </div>
  </Transition>
</template>

<style scoped>
.returns-drawer {
  position: fixed;
  inset: 0;
  z-index: var(--z-overlay);
  display: flex;
  justify-content: flex-end;
}
.returns-drawer__backdrop {
  position: absolute;
  inset: 0;
  border: 0;
  background: rgba(0, 0, 0, 0.35);
  cursor: pointer;
}
.returns-drawer__panel {
  position: relative;
  width: min(100%, 460px);
  height: 100%;
  background: var(--color-white);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}
.returns-drawer__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 24px 8px;
  flex-shrink: 0;
}
.returns-drawer__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  line-height: 1.2;
}
.returns-drawer__close {
  border: 0;
  background: none;
  cursor: pointer;
  color: var(--color-black);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  margin: -10px -10px 0 0;
  padding: 10px;
}
.returns-drawer__content {
  padding: 0 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.returns-drawer__intro {
  margin: 0;
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-text-secondary);
  line-height: 1.5;
}
.returns-drawer__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.returns-drawer__subtitle {
  margin: 0;
  font-size: 0.875rem;
  font-weight: var(--font-weight-bold);
  color: var(--color-black);
}
.returns-drawer__text {
  margin: 0;
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  line-height: 1.5;
}
.returns-drawer__text strong {
  font-weight: var(--font-weight-bold);
}
.returns-drawer__button {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 4px 0;
  padding: 14px 20px;
  background: var(--color-black);
  color: var(--color-white);
  border-radius: var(--radius-full);
  font-size: 0.875rem;
  text-decoration: none;
  transition: opacity 0.15s ease;
}
.returns-drawer__button:hover {
  opacity: 0.85;
}
.returns-drawer__contact {
  margin: 0;
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
}
.returns-drawer__contact strong {
  font-weight: var(--font-weight-bold);
}
.returns-drawer__contact a {
  color: var(--color-black);
}

.returns-drawer-enter-active,
.returns-drawer-leave-active {
  transition: opacity 0.2s ease;
}
.returns-drawer-enter-from,
.returns-drawer-leave-to {
  opacity: 0;
}

/* Mobile: bottom-sheet zamiast bocznego panelu (zgodnie z projektem) */
@media (max-width: 640px) {
  .returns-drawer {
    justify-content: stretch;
    align-items: flex-end;
  }
  .returns-drawer__panel {
    width: 100%;
    height: auto;
    max-height: 88vh;
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  }
}
</style>
